import React, { useState, useEffect, useRef } from 'react';

function MobileDoctorSearch({ user }) {
  const [searchId, setSearchId] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searching, setSearching] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [sendingRequest, setSendingRequest] = useState(false);
  const [error, setError] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [history, setHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`search_history_${user?.id}`) || '[]'); }
    catch { return []; }
  });

  useEffect(() => {
    if (!window.Html5Qrcode) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
      script.onload = () => setScannerReady(true);
      document.head.appendChild(script);
    } else {
      setScannerReady(true);
    }
  }, []);

  const startScanner = async () => {
    if (!scannerReady || !window.Html5Qrcode) { alert('Scanner not ready yet.'); return; }
    setShowScanner(true);
    setTimeout(async () => {
      try {
        html5QrRef.current = new window.Html5Qrcode('qr-reader');
        await html5QrRef.current.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => { stopScanner(); setSearchId(decodedText); handleSearch(decodedText); },
          () => {}
        );
      } catch {
        setShowScanner(false);
        setError('Camera access denied.');
      }
    }, 300);
  };

  const stopScanner = async () => {
    try { if (html5QrRef.current) { await html5QrRef.current.stop(); html5QrRef.current = null; } } catch { }
    setShowScanner(false);
  };

  const saveToHistory = (patient) => {
    const newHistory = [patient, ...history.filter(h => h.id !== patient.id)].slice(0, 10);
    setHistory(newHistory);
    localStorage.setItem(`search_history_${user?.id}`, JSON.stringify(newHistory));
  };

  const deleteFromHistory = (id, e) => {
    e.stopPropagation();
    const updated = history.filter(h => h.id !== id);
    setHistory(updated);
    localStorage.setItem(`search_history_${user?.id}`, JSON.stringify(updated));
  };

  const clearHistory = () => { setHistory([]); localStorage.removeItem(`search_history_${user?.id}`); };

  const handleSearch = async (uid) => {
    const id = uid || searchId.trim();
    if (!id) { setError('Please enter a Patient ID.'); return; }
    setSearching(true); setError(''); setSearchResult(null); setRequestSent(false);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Auth/search-patient?uid=${id}`);
      const data = await res.json();
      if (res.ok && data) { setSearchResult(data); saveToHistory(data); }
      else setError('No patient found with this ID.');
    } catch { setError('Cannot connect to server.'); }
    setSearching(false);
  };

  const handleSendRequest = async () => {
    setSendingRequest(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/Access/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ doctorId: user.id, patientUid: searchResult.patientUid })
      });
      if (res.ok) setRequestSent(true);
      else { const data = await res.json(); setError(data.message || 'Failed to send request.'); }
    } catch { setError('Cannot connect to server.'); }
    setSendingRequest(false);
  };

  const goBack = () => { setSearchResult(null); setRequestSent(false); setError(''); };

  // 1. QR SCANNER OVERLAY
  if (showScanner) return (
    <div className="fixed inset-0 z-[200] bg-black flex flex-col" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-center justify-between px-4 py-4">
        <button onClick={stopScanner} className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center active:scale-95">
          <span className="material-symbols-outlined text-white">close</span>
        </button>
        <p className="text-white font-bold text-base">Scan Patient QR Code</p>
        <div className="w-10"></div>
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        <div id="qr-reader" ref={scannerRef} style={{ width: '100%', maxWidth: '320px' }} className="rounded-2xl overflow-hidden"></div>
        <p className="text-white/70 text-sm mt-6 text-center">Point camera at patient's QR code</p>
      </div>
    </div>
  );

  // 2. PATIENT RESULT SCREEN (MATCHES SCREENSHOT 2)
  if (searchResult) return (
    <div className="pt-2 pb-6 px-4 space-y-4" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Header title with back arrow */}
      <div className="flex items-center gap-3 py-2">
        <button onClick={goBack} className="text-slate-900 active:scale-95 flex items-center justify-center">
          <span className="material-symbols-outlined font-extrabold text-xl">arrow_back</span>
        </button>
        <h1 className="text-xl font-extrabold text-[#0F172A] tracking-tight">Patient Found</h1>
      </div>

      {/* Patient Card */}
      <div className="bg-white rounded-3xl p-6 border border-slate-100/80 shadow-sm flex flex-col items-center text-center space-y-5">
        <div className="w-24 h-24 rounded-full bg-[#0B3C95] text-white flex items-center justify-center text-3xl font-black shadow-md border-4 border-blue-50">
          {(searchResult.name || 'P').charAt(0).toUpperCase()}
        </div>

        <div>
          <h2 className="text-xl font-black text-slate-900 tracking-tight">{searchResult.name}</h2>
          <p className="text-xs font-semibold text-slate-400 mt-0.5 tracking-wide font-mono">{searchResult.patientUid}</p>

          <div className="mt-2.5">
            <span className="bg-emerald-50 text-emerald-600 border border-emerald-200 text-[10px] font-bold px-3 py-1 rounded-md inline-flex items-center gap-1.5">
              <span className="material-symbols-outlined text-sm text-emerald-500" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
              Verified Patient
            </span>
          </div>
        </div>

        {/* Action Button */}
        {!requestSent ? (
          <button
            onClick={handleSendRequest}
            disabled={sendingRequest}
            className="w-full py-4 px-4 bg-gradient-to-r from-[#1D4ED8] via-[#8B5CF6] to-[#D946EF] text-white font-bold rounded-2xl flex items-center justify-center gap-2 shadow-lg shadow-purple-500/20 active:scale-[0.98] transition-all disabled:opacity-70 text-sm"
          >
            <span className="material-symbols-outlined text-xl">person_add</span>
            {sendingRequest ? 'Sending Request...' : 'Request Access to View Reports'}
          </button>
        ) : (
          <div className="w-full py-3.5 bg-emerald-50 rounded-2xl border border-emerald-200 flex items-center justify-center gap-2 text-emerald-700 font-bold text-sm">
            <span className="material-symbols-outlined text-xl text-emerald-600" style={{ fontVariationSettings: '"FILL" 1' }}>check_circle</span>
            Request Sent Successfully!
          </div>
        )}

        {error && <p className="text-red-500 text-xs font-semibold">{error}</p>}
      </div>

      {/* Search Another Button */}
      <button
        onClick={goBack}
        className="w-full py-3.5 bg-white border border-slate-200 text-slate-700 font-extrabold rounded-2xl text-xs flex items-center justify-center gap-2 shadow-xs active:scale-[0.98] transition-all"
      >
        <span className="material-symbols-outlined text-base text-slate-500">arrow_back</span>
        Search Another Patient
      </button>
    </div>
  );

  // 3. INITIAL SEARCH SCREEN (MATCHES SCREENSHOT 1)
  return (
    <div className="pt-2 pb-6 px-4 space-y-5" style={{ fontFamily: 'Inter, sans-serif' }}>
      
      {/* Title */}
      <div>
        <h1 className="text-2xl font-black text-[#0F172A] tracking-tight">Find Patient</h1>
        <p className="text-xs font-medium text-slate-400 mt-0.5">Search by ID or scan QR code</p>
      </div>

      {/* Primary Card */}
      <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm space-y-5">
        
        {/* Input & Search Button */}
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-slate-50 rounded-2xl px-3.5 py-3 flex items-center border border-slate-200 focus-within:border-[#0B3C95] transition-all">
            <span className="material-symbols-outlined text-slate-400 text-lg mr-2">search</span>
            <input
              type="text"
              placeholder="Enter Patient ID (MHJ...)"
              value={searchId}
              onChange={e => { setSearchId(e.target.value); setError(''); }}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              className="bg-transparent border-none p-0 text-xs font-medium text-slate-800 w-full placeholder-slate-400 focus:outline-none"
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={searching}
            className="bg-[#0B3C95] hover:bg-[#082d70] text-white font-bold px-5 py-3 rounded-2xl text-xs active:scale-95 transition-all shadow-sm disabled:opacity-50"
          >
            {searching ? '...' : 'Search'}
          </button>
        </div>

        {error && <p className="text-red-500 text-xs font-semibold text-center">{error}</p>}

        {/* OR Divider */}
        <div className="flex items-center gap-4">
          <div className="h-px bg-slate-100 flex-1"></div>
          <span className="text-[10px] font-extrabold text-slate-300 tracking-wider">OR</span>
          <div className="h-px bg-slate-100 flex-1"></div>
        </div>

        {/* QR Scanner Area */}
        <div className="border-2 border-dashed border-indigo-100 rounded-3xl p-6 flex flex-col items-center text-center space-y-3 bg-slate-50/40">
          <div className="w-14 h-14 bg-indigo-50/80 rounded-full flex items-center justify-center">
            <span className="material-symbols-outlined text-[#0B3C95] text-2xl">qr_code_scanner</span>
          </div>

          <div>
            <h3 className="font-extrabold text-slate-900 text-sm">Scan Patient QR Code</h3>
            <p className="text-[11px] text-slate-400 font-medium mt-0.5">Point camera at patient's MHJ QR code</p>
          </div>

          <button
            onClick={startScanner}
            className="px-6 py-3 bg-gradient-to-r from-[#1D4ED8] via-[#8B5CF6] to-[#D946EF] text-white font-extrabold text-xs rounded-2xl flex items-center justify-center gap-2 shadow-md shadow-purple-500/20 active:scale-95 transition-all"
          >
            <span className="material-symbols-outlined text-base">photo_camera</span>
            Open Camera
          </button>
        </div>
      </div>

      {/* Recent Searches */}
      {history.length > 0 && (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between px-1">
            <h4 className="text-xs font-bold text-slate-900">Recent Searches</h4>
            <button onClick={clearHistory} className="text-[11px] font-bold text-red-500 hover:text-red-600 active:scale-95">
              Clear All
            </button>
          </div>

          <div className="space-y-2">
            {history.map(h => (
              <div
                key={h.id}
                onClick={() => handleSearch(h.patientUid)}
                className="bg-white rounded-2xl p-3.5 flex items-center justify-between border border-slate-100 shadow-xs active:scale-[0.99] transition-all cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-blue-50 text-[#0B3C95] font-black text-base flex items-center justify-center">
                    {(h.name || 'P').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-bold text-slate-900 text-xs">{h.name}</p>
                    <p className="text-[10px] text-slate-400 font-mono mt-0.5">{h.patientUid}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      <span className="text-[9px] font-bold text-emerald-600 tracking-wider uppercase">Active</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 text-slate-300">
                  <span className="material-symbols-outlined text-lg">history</span>
                  <button
                    onClick={(e) => deleteFromHistory(h.id, e)}
                    className="hover:text-red-400 p-1 active:scale-95 transition-colors"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}

export default MobileDoctorSearch;